/*
Build instructions
==================

In its current state, this file cannot be loaded via an HTML <script>
tag (a) because it is written as an ES module and (b) because it
depends on the OpenLayers JavaScript library.  A prior conversion step
is needed using a so-called bundler.  I did the conversion on a Linux
system using the esbuild bundler running in a Docker container.  The
Dockerfile defining the Docker image is run/Dockerfile.  To build the
image, run the container, and generate the four files, app_min.css,
app_min.js, app_min.css.map, and app_min.js.map, run this command:

    run/build.sh

*/

import '../css/app.css';
import Feature from 'ol/Feature.js';
import Fill from 'ol/style/Fill.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { buffer, containsCoordinate, getCenter, intersects } from 'ol/extent';
import { easeOut } from 'ol/easing';
import OLMap from 'ol/Map.js';
import OSM from 'ol/source/OSM.js';
import Point from 'ol/geom/Point.js';
import Polygon from 'ol/geom/Polygon.js';
import { fromLonLat } from 'ol/proj';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import Text from 'ol/style/Text.js';
import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import View from 'ol/View.js';

import { makeAddressSearchAdapter } from './addressSearchAdapter.mjs';
import { makeAddressSearchModel } from './addressSearchModel.mjs';
import formatAddress from './formatAddress.mjs';

function debug(...args) {
    console.log(...args);
}

function debounce(fn, delay = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), delay);
    };
}

function make_event_bus() {
    // Maps event names to sets of handler functions.
    const handlers = new Map();

    return {
        on(event, fn) {
            if (!handlers.has(event)) {
                handlers.set(event, new Set());
            }
            handlers.get(event).add(fn);
            // Return function to unsubscribe.
            return () => this.off(event, fn);
        },

        off(event, fn) {
            const set = handlers.get(event);
            if (!set) {
                return;
            }
            set.delete(fn);
            if (set.size === 0) {
                handlers.delete(event);
            }
        },

        once(event, fn) {
            const wrapper = (...args) => {
                fn(...args);
                this.off(event, wrapper);
            };
            this.on(event, wrapper);
            // Return function to unsubscribe.
            return () => this.off(event, wrapper);
        },

        publish(event, ...args) {
            const set = handlers.get(event);
            if (!set) {
                return;
            }
            const fns = Array.from(set);
            setTimeout(() => {
                for (const fn of fns) {
                    try {
                        fn(...args);
                    }
                    catch (err) {
                        console.error('event listener error for', event, err);
                    }
                }
            }, 0);
        },

        clear(event) {
            if (event) {
                handlers.delete(event);
            }
            else {
                handlers.clear();
            }
        },
    };
};

function load_file_config(response) {
    if (!response.ok) {
        throw new Error(`Fetch of config.json failed: ${response.statusText}`);
    }
    return response.json();
}

function update_app_config(file_config, app) {
    app.configMap = app.configMap
                  ? {...app.configMap, ...file_config}
                  : file_config;
    return app;
}

function load_grid_csv(response) {
    if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
    }
    return response.text();
}

function make_grid_feature(id, row, col, bbox, step) {
    // [bbox.xmin, bbox.ymax] are the grid's bounding boxes top-left corner.
    // [x, y] is the grid cell's top-left corner.  row and col indexes are
    // 0-based.  y-coordinates decrease southwards.  step corresponds to the
    // side length of the grid cell squares.
    const x = bbox.xmin + col * step;
    const y = bbox.ymax - row * step;
    const coords = [
        [ x        , y        ],
        [ x + step , y        ],
        [ x + step , y - step ],
        [ x        , y - step ],
        [ x        , y        ],
    ];
    const feature = new Feature(new Polygon([coords]));
    feature.setId(id);
    return feature;
}

function parse_grid_features(response_text, city, config) {
    const lines = response_text.split(/\r?\n/);
    lines.shift();  // skip header
    const features = lines.map(line => {
        const [id, row, col] = line.split(',').map(Number);
        return make_grid_feature(id, row, col,
            config[city].grid_bbox, config.grid_step);
    });
    return features;
}

function load_grid(city, app_url, config) {
    return fetch(app_url + `data/${city}_grid.csv`)
        .then(load_grid_csv)
        .then(text => parse_grid_features(text, city, config));
}

function get_grid_layer(city) {
    const layer = app.stateMap.gridLayer[city];
    if (layer !== null) {
        return Promise.resolve(layer);
    }
    return make_grid_layer(city, app.url, app.configMap);
}

async function make_grid_layer(city, app_url, config) {

    // Load features right now, not when the map needs them.
    const features = await load_grid(city, app_url, config);

    const source = new VectorSource({
        features,
        overlaps : false,
    });

    const layer = new VectorLayer({
        source,
    });

    layer.set('type', 'grid');

    app.stateMap.gridLayer[city] = layer;

    return layer;
}

function make_grid_label_style(value, geom) {
    return new Style({
        geometry : new Point(getCenter(geom.getExtent())),
        text     : new Text({
            text  : String(value),
            font  : '10px sans-serif',
            scale : 1.8,
        }),
    });
}

function make_grid_layer_styles() {
    return {
        'value-mode' : make_grid_layer_value_style(),
        'diff-mode'  : make_grid_layer_diff_style(),
        'map-mode'   : make_grid_layer_map_style(),
    };
}

function make_grid_layer_value_style() {

    const make_background_style = (cell, topic) => {
        const color   = app.configMap.topic[topic].color.value;
        // -1 because bucket is 1-based while array indexing is 0-based.
        const [r,g,b] = app.configMap.colors[color][cell.bucket-1];
        const alpha   = app.configMap.topic[topic].color.alpha;
        const rgba    = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        return new Style({
            'fill': new Fill({ color : rgba }),
        });
    };

    const make_styles = (feature, resolution) => {
        const topic  = app.stateMap.topic;
        const id     = feature.getId();
        const city   = app.stateMap.city;
        const data   = app.stateMap.data[city][topic];
        const cell   = data.map.get(id);
        const styles = [
            make_background_style(cell, topic),
        ];
        if (resolution < app.stateMap.max_resolution.label) {
            styles.push(make_grid_label_style(cell.value, feature.getGeometry()));
        }
        return styles;
    };

    return (feature, resolution) => {
        try {
            return make_styles(feature, resolution);
        }
        catch (_) {
            return;
        }
    };
}

function make_grid_layer_diff_style() {

    const make_background_style = (cell, ref_cell, topic) => {
        const diff = cell.bucket - ref_cell.bucket;
        let r, g, b;
        if (diff !== 0) {
            const which = diff < 0 ? 'less' : 'more';
            const color = app.configMap.topic[topic].color.difference[which];
            // Note that not applying a -1 correction to the index ensures that
            // white, which is expected to appear at index 0 of all color ramps,
            // is not chosen when there is a difference.  White is already used
            // for "no difference" in the else-branch.
            [r, g, b] = app.configMap.colors[color][Math.abs(diff)];
        }
        else {
            [r, g, b] = app.configMap.colors.white[0];
        }
        const alpha = app.configMap.topic[topic].color.alpha;
        const rgba  = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        return new Style({
            'fill': new Fill({ color : rgba }),
        });
    };

    const make_styles = (feature, resolution) => {
        const topic    = app.stateMap.topic;
        const id       = feature.getId();
        const city     = app.stateMap.city;
        const data     = app.stateMap.data[city][topic];
        const ref_id   = app.stateMap.ref_grid_cell.getId();
        const cell     = data.map.get(id);
        const ref_cell = data.map.get(ref_id);
        const diff     = id === ref_id ? 0 : cell.value - ref_cell.value;
        const styles   = [
            make_background_style(cell, ref_cell, topic),
        ];
        if (resolution < app.stateMap.max_resolution.label) {
            styles.push(make_grid_label_style(format_diff(diff), feature.getGeometry()));
        }
        return styles;
    };

    return (feature, resolution) => {
        try {
            return make_styles(feature, resolution);
        }
        catch (_) {
            return;
        }
    };
}

function is_integer(x) {
    return Math.abs(x - Math.round(x)) < 1e-9;
}

function format_diff(x) {
    return x.toFixed(is_integer(x) ? 0 : 1);
}

function make_grid_layer_map_style() {

    return (feature, resolution) => {
        // Do not render the grid at all in map mode.
        return;
    };
}

function make_highlight_layer() {

    const style = new Style({
        stroke : new Stroke({
            color : app.configMap.highlight_stroke_color,
            width : app.configMap.highlight_stroke_width,
        }),
    });

    return new VectorLayer({
        source        : new VectorSource(),
        style,
        maxResolution : app.stateMap.max_resolution.highlight,
        zIndex        : 99,
    });
}

function load_topic_data(city, topic) {
    if (topic === null) {
        return;
    }
    const data = app.stateMap.data[city][topic];
    if (data) {
        return Promise.resolve(data);
    }
    const url = app.url + `data/${city}_${topic}.csv`;
    // Split data into as many buckets as there are color ramp values.
    const value_color = app.configMap.topic[topic].color.value;
    const diff_colors = app.configMap.topic[topic].color.difference;
    const colors = app.configMap.colors;
    const N = Math.min(
        colors[value_color     ].length,
        colors[diff_colors.less].length,
        colors[diff_colors.more].length,
    );
    return fetch(url)
        .then(load_topic_csv)
        .then(parse_topic_data)
        .then(rows => assign_bucket(rows, N))
        .then(map_id_to_data)
        .then(data => save_topic_data(city, topic, data));
}

function load_topic_csv(response) {
    if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
    }
    return response.text();
}

function parse_topic_data(response_text) {
    const lines = response_text.split(/\r?\n/);
    lines.shift();  // skip header
    const rows = [];
    lines.forEach(line => {
        if (line === '') {
            return;
        }
        const [id, value] = line.split(',').map(Number);
        rows.push([id, value]);
    });
    return rows;
}

function assign_bucket(rows, N) {
    const n = rows.length;
    if (n === 0) {
        return rows;
    }
    if (N <= 1) {
        for (const row of rows) {
            row.push(1);
        }
        return rows;
    }

    // Sort values (row format is [id, value]).
    const sorted = rows.map(r => r[1]);
    sorted.sort((a, b) => a - b);

    // Compute ranks.
    const ranks = new Map();
    for (let i = 0; i < n; i++) {
        if (!ranks.has(sorted[i])) {
            ranks.set(sorted[i], i);
        }
    }

    // Append bucket (1..N) to each row.
    for (const row of rows) {
        const x = row[1];
        const rank = ranks.get(x);
        let bucket = Math.floor((rank / n) * N) + 1;
        if (bucket > N || rank === n - 1) {
            bucket = N;
        }
        row.push(bucket);
    }

    return rows;
}

// Return map from feature ID to { value, bucket }.
function map_id_to_data(rows) {
    const data = new Map();
    for (const row of rows) {
        const [id, value, bucket] = row;
        data.set(id, { value, bucket });
    }
    return data;
}

function save_topic_data(city, topic, data) {
    let min = Infinity, max = -Infinity;
    data.forEach(({ value, bucket }) => {
        min = Math.min(min, value);
        max = Math.max(max, value);
    });
    return app.stateMap.data[city][topic] = {
        map : data,
        min,
        max,
    };
}

function refresh_grid_style() {
    const city  = app.stateMap.city;
    const layer = app.stateMap.gridLayer[city];
    const mode  = app.stateMap.gridMode;
    const style = app.stateMap.gridStyle[mode];
    layer.setStyle(style);
}

// Compute a resolution for the grid layers that ensures that at most max_cells
// grid cells, each with a side length of app.configMap.grid_step meters, are
// shown on the map in left-to-right and bottom-to-top direction.
function compute_max_grid_resolution(max_cells) {
    const length = app.configMap.grid_step;
    const [map_width, map_height] = app.stateMap.map.getSize();
    const max_res = Math.min(
        (max_cells * length) / map_width,  // max x-resolution
        (max_cells * length) / map_height  // max y-resolution
    );
    return max_res;
}

function make_map() {
    const extent = app.configMap.Bulgaria.extent;
    const padding = [20, 20, 20, 20];
    const view = new View({
        projection : app.configMap.projection,
        // Dummy values overridden by fit() below.
        center     : getCenter(extent),
        zoom       : 10,
    });
    const map = new OLMap({
        target: 'map',
        layers: [
            new TileLayer({
                source: new OSM(),
            }),
        ],
        view,
    });
    map.set('layer_set', new Set());
    view.fit(app.configMap.Bulgaria.extent, { padding : [20, 20, 20, 20] });
    return map;
}

function initStateMap() {
    const cities = app.stateMap.cities;
    const topics = app.configMap.topics;
    for (const city of cities) {
        app.stateMap.gridLayer[city] = null;
        app.stateMap.data     [city] = {};
        for (const topic of topics) {
            app.stateMap.data[city][topic] = null;
        }
    }

    app.stateMap.map = make_map(app.configMap);

    app.stateMap.max_resolution.label = compute_max_grid_resolution(
        app.configMap.grid_label_max_cells);
    app.stateMap.max_resolution.highlight = compute_max_grid_resolution(
        app.configMap.grid_highlight_max_cells);

    app.domMap.mode_buttons.querySelectorAll('button').forEach(el => {
        if (!el.disabled) {
            app.stateMap.gridMode = el.id;
        }
    });

    const searchAdapter = makeAddressSearchAdapter();
    const eventBus = app.event;
    app.stateMap.addressSearch = makeAddressSearchModel({
        searchAdapter, eventBus,
    });
}

function initDomMap() {
    const cities = app.stateMap.cities;
    for (const city of cities) {
        app.domMap.city_button[city] = document.getElementById(city);
    }
    app.domMap.other_settings_div = document.getElementById('other_settings');
    app.domMap.street_input = document.querySelector('#street input');
    app.domMap.street_select = document.querySelector('#street .custom-select');
    app.domMap.street_candidates = document.querySelector('#street ul');
    app.domMap.street_div = document.getElementById('street');
    app.domMap.topic_div = document.getElementById('topic');
    app.domMap.mode_div = document.getElementById('mode');
    app.domMap.mode_buttons = document.getElementById('mode-buttons');
    app.domMap.diff_mode = document.getElementById('diff-mode');
    app.domMap.value_mode = document.getElementById('value-mode');
    app.domMap.map_mode = document.getElementById('map-mode');
    app.domMap.map_div = document.getElementById('map');
}

function initUI() {
    app.domMap.street_input.value = '';
    app.domMap.diff_mode.disabled = true;
    app.domMap.value_mode.disabled = true;
}

function onChangedCity(city) {
    debug(`onChangedCity('${city}')`);
    const previous_city = app.stateMap.city;
    app.stateMap.city = city;

    // This is the first time the user selected a city.
    if (previous_city === null) {
        // Show map.
        app.domMap.map_div.classList.remove('hidden');
        // Create grid layer styles.
        app.stateMap.gridStyle = make_grid_layer_styles();
    }
    else if (city !== previous_city) {
        clearStreetSearch();
    }

    // Mark city as selected.
    for (const city of app.stateMap.cities) {
        app.domMap.city_button[city].classList.remove('selected');
    }
    app.domMap.city_button[city].classList.add('selected');

    Promise.all([
            get_grid_layer(city),
            load_topic_data(city, app.stateMap.topic),
        ])
        .then(([grid_layer]) => {

            add_layer(app.stateMap.map, grid_layer, `${city}:grid`);
            set_grid_layer_style(grid_layer);

            // If city changed, remove the current but now invalid ref grid cell
            // and switch to map mode so that the user can find a location of
            // interest in the new city without the city map being obstructed by
            // topic data.
            if (city !== previous_city) {
                app.stateMap.ref_grid_cell = null;
                app.event.publish('mode:changed', 'map-mode');
            }

            const callback = () => app.domMap.other_settings_div.classList.add('open');
            zoom_to_city({ city, previous_city, callback })
        });
}

function clearStreetSearch() {
    app.domMap.street_input.value = '';
    app.domMap.street_input.dispatchEvent(
        new InputEvent('input', { data : '' }));
}

function add_layer(map, layer, key) {
    const layer_set = map.get('layer_set');
    if (!layer_set.has(key)) {
        layer_set.add(key);
        map.addLayer(layer);
    }
}

function get_grid_cell_feature(layer, coord) {
    const source = layer.getSource();
    const extent = [coord[0],      coord[1],
                    coord[0]+1e-6, coord[1]+1e-6];
    const features = source.getFeaturesInExtent(extent);
    if (features.length == 0) {
        return;
    }
    return features[0];
}

function highlight_feature(feature) {
    let layer = app.stateMap.highlightLayer;
    if (layer === null) {
        layer = app.stateMap.highlightLayer = make_highlight_layer();
        app.stateMap.map.addLayer(layer);
    }
    layer.getSource().clear(true);
    layer.getSource().addFeature(feature);
}

function set_grid_layer_style(layer) {
    const mode = app.stateMap.gridMode;
    const style = app.stateMap.gridStyle[mode];
    layer.setStyle(style);
}

function zoom_to_city({ city, previous_city, callback = () => {} }) {
    // city  prev  action
    //    A     A  do nothing
    //    A     B  zoom to Bulgaria, then to A's extent
    //    A  null  zoom directly to A's extent

    const view = app.stateMap.map.getView();
    const padding = [20, 20, 20, 20];
    const easing = easeOut;
    const zoom_fns = [];

    // Not first city and city changed.
    if (previous_city !== null && city !== previous_city) {
        // Zoom to Bulgaria's extent.
        app.stateMap.gridLayer[previous_city].setVisible(false);
        zoom_fns.push(callback => {
            const extent = app.configMap.Bulgaria.extent;
            view.fit(extent, { padding, callback, duration : 3000, easing });
        });
    }

    // Zoom to extent of new city.
    zoom_fns.push(callback => {
        app.stateMap.gridLayer[city].setVisible(true);
        const duration = previous_city !== city ? 3000 : 0;
        const extent = app.configMap[city].extent;
        view.fit(extent, { padding, callback, duration, easing });
    });

    const zoom_fn = zoom_fns.reduceRight((next, fn) => () => fn(next), callback);
    zoom_fn();
}

function onChangedTopic(topic) {
    debug(`onChangedTopic('${topic}')`);
    const previous_topic = app.stateMap.topic;
    if (previous_topic === null) {
        // Enable diff and value mode buttons.
        app.domMap.diff_mode.disabled = false;
        app.domMap.value_mode.disabled = false;
        // Switch grid layer to diff mode or value mode.
        app.event.publish(
            'mode:changed',
            app.stateMap.ref_grid_cell ? 'diff-mode' : 'value-mode',
        );
    }
    app.stateMap.topic = topic;
    load_topic_data(app.stateMap.city, topic)
        .then(() => {
            refresh_grid_style();
            if (app.stateMap.ref_grid_cell) {
                zoom_to_grid_cell(app.stateMap.ref_grid_cell);
            }
        });
}

function onClickModeButton(event) {
    const mode = event.target.id;
    if (mode !== app.stateMap.gridMode) {
        app.event.publish('mode:changed', mode);
    }
}

function onPublishAddressResults(data) {
    clearAddressCandidates();
    if (!data.error) {
        showAddressCandidates(data.addresses);
    }
}

function clearAddressCandidates() {
    app.domMap.street_candidates.innerHTML = '';
}

function showAddressCandidates(addresses) {
    const items = [];
    if (addresses.length === 0) {
        items.push(makeAddressListItem({
            label : 'Sorry, I do not know this address.',
        }));
    }
    else {
        for (const adr of addresses) {
            try {
                const formatted = formatAddress(adr);
                items.push(makeAddressListItem({
                    label : formatted,
                    data  : JSON.stringify(adr),
                }));
            }
            catch (error) {
                console.log(error);
            }
        }
    }
    app.domMap.street_candidates.append(...items);
    app.domMap.street_select.classList.add('open');
}

function makeAddressListItem({ label, data } = {}) {
    const li = document.createElement('li');
    const span = document.createElement('span');
    span.innerHTML = label;
    li.appendChild(span);
    li.dataset.value = data || '';
    return li;
}

function onInputStreet(event) {
    app.stateMap.addressSearch.search({
        query   : event.target.value.trim(),
        city    : app.stateMap.city,
        country : 'Bulgaria',
    });
}

function onClickStreetCandidate(event) {
    const li = event.target.closest('li');
    if (!li.dataset.value) {
        return;
    }
    const adr = JSON.parse(li.dataset.value);
    app.domMap.street_input.value = formatAddress(adr);
    app.domMap.street_select.classList.remove('open');
    const coord = fromLonLat([adr.lon, adr.lat], app.configMap.projection);
    zoom_to_coord(coord, () => showGridCell(coord));
}

function showGridCell(coord) {
    const city     = app.stateMap.city;
    const source   = app.stateMap.gridLayer[city].getSource();
    const features = source.getFeaturesAtCoordinate(coord);
    if (features.length === 0) {
        return;
    }
    const ref_grid_cell = app.stateMap.ref_grid_cell;
    const grid_cell = features[0];
    if (ref_grid_cell === null || grid_cell.getId() !== ref_grid_cell.getId()) {
        app.stateMap.ref_grid_cell = grid_cell;
        highlight_feature(grid_cell);
        zoom_to_grid_cell(grid_cell);
        if (app.stateMap.topic) {
            app.event.publish('mode:changed', 'diff-mode');
        }
    }
    // User clicked on ref grid cell and we have a topic.
    else if (app.stateMap.topic) {
        if (app.stateMap.gridMode !== 'diff-mode') {
            app.event.publish('mode:changed', 'diff-mode');
        }
    }
}

function onClickMap(event) {
    const coord = app.stateMap.map.getCoordinateFromPixel(event.pixel);
    showGridCell(coord);
}

function zoom_to_grid_cell(feature) {
    const size            = app.stateMap.map.getSize();
    const view            = app.stateMap.map.getView();
    const geom            = feature.getGeometry();
    const view_extent     = view.calculateExtent(size);
    const feature_extent  = geom.getExtent();
    const feature_in_view = intersects(view_extent, feature_extent);
    const bad_resolution  = view.getResolution()
                          > app.stateMap.max_resolution.label;
    if (!feature_in_view || bad_resolution) {
        zoom_to_coord(getCenter(feature_extent));
    }
}

function zoom_to_coord(coord, callback = () => {}) {
    const view = app.stateMap.map.getView();
    const duration = 2000;
    view.animate({
        center     : coord,
        // Zoom in enough for labels to be shown.
        resolution : app.stateMap.max_resolution.label - .1,
        duration,
    });
    setTimeout(callback, duration);
}

function select_mode_button(mode) {
    app.domMap.mode_buttons.querySelectorAll('button').forEach(el => {
        if (el.id === mode) {
            el.classList.add('selected');
        }
        else {
            el.classList.remove('selected');
        }
    });
}

function get_closest_grid_cell(coord) {
    const city    = app.stateMap.city;
    const source  = app.stateMap.gridLayer[city].getSource();
    const feature = source.getClosestFeatureToCoordinate(
        coord,
        feature => {
            const extent = feature.getGeometry().getExtent();
            return containsCoordinate(extent, coord);
        },
    );
    if (!feature) {
        throw new Error(`failed to find ${city} grid cell closest to ${coords}`);
    }
    return feature;
}

function onChangedMode(new_mode) {
    debug(`onChangedMode('${new_mode}')`);
    const previous_mode = app.stateMap.gridMode;
    if (previous_mode === new_mode) {
        if (new_mode === 'diff-mode') {
            refresh_grid_style();
        }
        return;
    }
    if (new_mode === 'diff-mode' && !app.stateMap.ref_grid_cell) {
        const view_center = app.stateMap.map.getView().getCenter();
        try {
            const grid_cell = get_closest_grid_cell(view_center);
            app.stateMap.ref_grid_cell = grid_cell;
            highlight_feature(grid_cell);
            zoom_to_grid_cell(grid_cell);
        }
        catch (error) {
            console.log(error);
            // Can't change to diff mode without reference grid cell.
            return;
        }
    }
    select_mode_button(new_mode);
    app.stateMap.gridMode = new_mode;
    refresh_grid_style();
}

function initListeners() {
    for (const city of app.stateMap.cities) {
        app.domMap.city_button[city].addEventListener(
            'click', () => {
                if (city !== app.stateMap.city) {
                    app.event.publish('city:changed', city);
                }
            }
        );
    }

    app.domMap.mode_buttons.addEventListener('click', onClickModeButton);

    app.event.on('address:results', onPublishAddressResults);
    app.domMap.street_input.addEventListener('input', debounce(onInputStreet, 300));
    app.domMap.street_candidates.addEventListener('click', onClickStreetCandidate);

    app.stateMap.map.on('click', onClickMap);
}

function make_topic_item(topic) {
    const li = document.createElement('li');
    const span = document.createElement('span');
    span.innerHTML = app.configMap.topic[topic].label;
    li.appendChild(span);
    li.dataset.value = topic;
    return li;
}

function initTopicDropdown() {
    const topics = app.configMap.topics;
    const div = document.getElementById('topic');
    const custom = div.querySelector('.custom-select');
    const button = custom.querySelector('button');
    const button_span = button.querySelector('span');
    const list = custom.querySelector('ul');
    const items = topics.map(topic => {
        return list.appendChild(make_topic_item(topic));
    });

    function open() {
        custom.classList.add('open');
        list.focus();
    }

    function close() {
        custom.classList.remove('open');
    }

    function select(item) {
        const topic = item.dataset.value;
        button_span.innerHTML = app.configMap.topic[topic].label;
        button.classList.add('selected');
        close();
        app.event.publish('topic:changed', topic);
    }

    button.addEventListener('click',
        () => (custom.classList.contains('open') ? close() : open()));

    items.forEach(item => item.addEventListener('click', () => select(item)));

    // Make clicks outside of the dropdown close the dropdown.
    document.addEventListener('click', e => {
        if (!custom.contains(e.target)) {
            close();
        }
    });
}

// ==== Initialization =========================================================

const app = window.app;

app.stateMap = {
    cities         : [ 'Sofia', 'Plovdiv', 'Burgas', 'Varna', 'Ruse' ],
    map            : null,
    city           : null,
    streetName     : null,
    topic          : null,
    ref_grid_cell  : null,
    data           : {},  // x[city][topic]
    gridLayer      : {},  // x[city]
    gridStyle      : {},  // x[grid_mode]
    gridMode       : null,  // matches IDs of mode buttons
    max_resolution : { label : null, highlight : null },
    highlightLayer : null,
    addressSearch  : null,
};

app.domMap = {
    city_button        : {},  // x[city]
    other_settings_div : null,
    street_input       : null,
    street_div         : null,
    topic_div          : null,
    mode_div           : null,
    mode_buttons       : null,
    map_div            : null,
};

app.event = make_event_bus();

app.event.on('city:changed', onChangedCity);
app.event.on('topic:changed', onChangedTopic);
app.event.on('mode:changed', onChangedMode);

fetch(app.url + 'config.json')
    .then(load_file_config)
    .then(file_config => update_app_config(file_config, app))
    .then(() => {
        initDomMap();
        initStateMap();
        initUI();
        initListeners();
        initTopicDropdown();
    })
    .catch(err => {
        console.log('Initialization failed:', err);
    });
